import {
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	computed,
	effect,
	inject,
	Injector,
	signal,
	untracked,
	viewChild,
	viewChildren,
	input
} from '@angular/core'
import { TranslocoDirective } from '@jsverse/transloco'
import { injectLogger } from '@/services/logger.service'
import { SearchInputComponent } from '@/features/search/components/search-input/search-input.component'
import { RecentSearchesComponent } from '@/features/search/components/recent-searches/recent-searches.component'
import { OverlayPanelComponent } from '@/shared/components/containers/overlay-panel/overlay-panel.component'
import { SearchModalService } from '@/services/search-modal.service'
import { PrematchService } from '@/features/betting-offer/prematch/prematch.service'
import { RecentSearchesService } from '@/services/recent-searches.service'
import { CasinoGameIntentService } from '@/features/casino/casino-game-intent.service'
import { NavigationEnd, NavigationStart, Router } from '@angular/router'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { filter } from 'rxjs'
import { appPages } from '@/routes/app-pages'
import { isCasinoGameUrl } from '@/routes/app.routes'
import { withMinDelay } from '@/utils/promises'
import { SearchItem, SearchView, EMPTY_SEARCH_VIEW } from '@/features/search/search.model'
import { SearchScope, SearchSource } from '@/features/search/search.source'
import { SportsbookSearchSource } from '@/features/search/sources/sportsbook-search.source'
import { CasinoSearchSource } from '@/features/search/sources/casino-search.source'
import { MatchPanelCardComponent } from './cards/match-panel-card.component'
import { TournamentPanelCardComponent } from './cards/tournament-panel-card.component'
import { GamePanelCardComponent } from './cards/game-panel-card.component'
import { SearchScopeTabsComponent } from '@/features/search/components/search-scope-tabs/search-scope-tabs.component'
import { SearchGameTileComponent } from '@/features/search/components/search-game-tile/search-game-tile.component'
import { CasinoGameItem } from '@/features/casino/casino.model'
import { LayoutService } from '@/services/layout.service'

/** App-wide search: an overlay holding the field, recent searches and results. */
@Component({
	selector: 'search-panel',
	templateUrl: './search-panel.component.html',
	styleUrl: './search-panel.component.css',
	imports: [
		TranslocoDirective,
		OverlayPanelComponent,
		SearchInputComponent,
		RecentSearchesComponent,
		MatchPanelCardComponent,
		TournamentPanelCardComponent,
		GamePanelCardComponent,
		SearchScopeTabsComponent,
		SearchGameTileComponent
	],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class SearchPanelComponent {
	// Two, because the casino search call answers nothing shorter than that.
	readonly minSearchChars = input(2)

	protected readonly view = signal(EMPTY_SEARCH_VIEW)
	protected readonly isLoading = signal(false)
	protected readonly selectedIndex = signal(-1)
	protected readonly hasResults = computed(() => this.view().totalCount > 0)
	protected readonly recentTerms = inject(RecentSearchesService).terms
	// Desktop lays game results out as a grid of tiles, a phone as a list of rows.
	protected readonly isDesktop = inject(LayoutService).isDesktop
	/** The empty state's Suggested Games: casino only, since they are games. */
	protected readonly suggestedGames = signal<CasinoGameItem[]>([])
	protected readonly suggestedGamesVisible = computed(() =>
		this.searchModalService.scope() === 'casino' && this.suggestedGames().length > 0
	)
	// Stays up while typing, saying so when there are none; only results replace it.
	protected readonly recentSearchesVisible = computed(() => !this.hasResults())

	private readonly searchInput = viewChild.required(SearchInputComponent)
	private readonly resultRows = viewChildren<ElementRef<HTMLElement>>('resultRow')
	private readonly logger = injectLogger('SearchPanelComponent')
	private requestId = 0
	// What the results on screen were searched for, so a scope tab can ask again.
	private lastTerm = ''
	// lastTerm's results per scope, so going back and forth between the tabs asks once for each.
	private readonly resultsByScope = new Map<SearchScope, Promise<SearchView>>()
	private wasOpen = false
	// Set while a game launched from the panel runs: the URL to come back to,
	// where the panel reopens as it was. Null otherwise.
	private resumeUrl: string | null = null
	private resuming = false

	// The panel is in the app shell, so it is built on every page. Casino
	// dependencies are resolved through this on first casino use instead of
	// being injected, which would start the casino services on Sportsbook too.
	private readonly injector = inject(Injector)

	constructor(
		public readonly searchModalService: SearchModalService,
		private router: Router,
		private prematchService: PrematchService,
		private recentSearchesService: RecentSearchesService,
		private sportsbookSearchSource: SportsbookSearchSource
	) {

		// Leaving the page leaves its search: a side-nav link, a result, the back
		// button. A game is the exception: the panel steps aside for it untouched,
		// and comes back as it was once the game closes.
		this.router.events
			.pipe(filter((event) => event instanceof NavigationStart), takeUntilDestroyed())
			.subscribe((event) => {
				if (!this.searchModalService.isOpen()) return

				if (isCasinoGameUrl(event.url)) {
					this.resumeUrl = this.router.url
					this.searchModalService.close()
					return
				}

				this.handleClose()
			})

		this.router.events
			.pipe(filter((event) => event instanceof NavigationEnd), takeUntilDestroyed())
			.subscribe((event) => this.resumeAfterGame(event.urlAfterRedirects))

		effect(() => {
			const open = this.searchModalService.isOpen()

			if (open) {
				// The field can't take focus until the overlay's visibility has flipped.
				// Not when returning from a game: that would raise a phone's keyboard
				// over the results the player came back to.
				if (!this.resuming) setTimeout(() => this.searchInput().focus())
				this.resuming = false
				if (untracked(() => this.searchModalService.scope()) === 'casino') this.loadSuggestedGames()
			} else if (this.wasOpen && this.resumeUrl === null) {
				// Closed from outside too (the profile menu does), so the next opening
				// starts empty rather than on the last term and its results.
				untracked(() => this.searchInput().clear())
			}

			this.wasOpen = open
		})
	}

	/**
	 * Back on the page the game was launched from: reopen as it was, same term,
	 * results, scope and scroll. Anywhere else but the game, the stepped-aside
	 * search is dropped, as leaving the page drops it.
	 */
	private resumeAfterGame(url: string): void {
		if (this.resumeUrl === null || isCasinoGameUrl(url)) return

		const backOnPage = url === this.resumeUrl
		this.resumeUrl = null

		if (backOnPage) {
			this.resuming = true
			this.searchModalService.open(this.searchModalService.scope())
		} else {
			this.searchInput().clear()
		}
	}

	/** clear() empties the field and emits (cleared), which resets the results. */
	protected handleClose(): void {
		this.searchInput().clear()
		this.searchModalService.close()
	}

	protected handleSearch(term: string): void {
		if (term !== this.lastTerm) this.resultsByScope.clear()
		this.lastTerm = term
		void this.doSearch(term)
	}

	protected handleCleared(): void {
		this.lastTerm = ''
		this.resultsByScope.clear()
		this.requestId++
		this.view.set(EMPTY_SEARCH_VIEW)
		this.selectedIndex.set(-1)
		this.isLoading.set(false)
	}

	/** Same term, the other domain's results. */
	protected handleScopeChange(scope: SearchScope): void {
		this.searchModalService.scope.set(scope)

		if (scope === 'casino') this.loadSuggestedGames()

		if (this.lastTerm) void this.doSearch(this.lastTerm)
	}

	protected handleNavigate(direction: number): void {
		const lastIndex = this.view().totalCount - 1
		if (lastIndex < 0) {
			return
		}
		const nextIndex = Math.max(-1, Math.min(lastIndex, this.selectedIndex() + direction))
		this.selectedIndex.set(nextIndex)
		// Rows are queried in document order, so the flat index addresses them directly.
		this.resultRows()[nextIndex]?.nativeElement.scrollIntoView({ block: 'nearest' })
	}

	protected handleSubmit(): void {
		const item = this.view().flatItems[this.selectedIndex()]
		if (item) {
			this.handleSelect(item)
		}
	}

	protected handleSelect(item: SearchItem): void {
		switch (item.type) {
			case 'match':
				void this.router.navigate([appPages.events.href, item.data.matchId])
				break
			case 'tournament':
				this.prematchService.toggleTournamentSelection(item.data.tournamentId, true)
				break
			case 'game':
				this.openGame(item.data)
				return
		}
		this.handleClose()
	}

	/** A suggested game opens like a tap on a lobby card, pre-launch screen and all. */
	protected handleSuggestedGame(game: CasinoGameItem): void {
		this.openGame(game)
	}

	protected handleRecentSelected(term: string): void {
		this.searchInput().setTerm(term)
	}

	protected handleRecentRemoved(term: string): void {
		this.recentSearchesService.remove(term)
	}

	protected handleRecentClearAll(): void {
		this.recentSearchesService.clear()
	}

	/**
	 * The casino page behind the panel opens its own pre-launch screen, above the
	 * panel, so a pick behaves like a tap on a card - demo included. The panel
	 * stays: closing that screen returns to the results. Starting the game
	 * navigates, and that closes the panel.
	 */
	private openGame(game: CasinoGameItem): void {
		this.injector.get(CasinoGameIntentService).request(game)
	}

	// The source keeps a list that has games, so reopening is free.
	private loadSuggestedGames(): void {
		void this.injector.get(CasinoSearchSource).suggestedGames()
			.then((games) => this.suggestedGames.set(games))
	}

	/** Whichever domain the trigger asked for; see SearchModalService.scope. */
	private source(): SearchSource {
		return this.searchModalService.scope() === 'casino'
			? this.injector.get(CasinoSearchSource)
			: this.sportsbookSearchSource
	}

	private results(term: string): Promise<SearchView> {
		const scope = this.searchModalService.scope()
		const cached = this.resultsByScope.get(scope)
		if (cached) return cached

		const pending = withMinDelay(this.source().search(term))
		this.resultsByScope.set(scope, pending)
		// Not kept when it fails, so the tab can ask again.
		pending.catch(() => {
			if (this.resultsByScope.get(scope) === pending) this.resultsByScope.delete(scope)
		})
		return pending
	}

	private async doSearch(term: string): Promise<void> {
		// A slower earlier request must not overwrite the results of a newer one.
		const id = ++this.requestId
		this.isLoading.set(true)
		try {
			const view = await this.results(term)
			if (id !== this.requestId) {
				return
			}
			this.view.set(view)
			this.selectedIndex.set(-1)
			if (view.totalCount > 0) {
				this.recentSearchesService.add(term)
			}
		} catch (err) {
			this.logger.error('search:', err)
		} finally {
			if (id === this.requestId) {
				this.isLoading.set(false)
			}
		}
	}
}
